<template>
  <div :class="[
      'preview-content',
      user?.status == null ? '' : 'extended'
    ]"
  >
    <div class="content-buttons-wrap bg-blur">
      <NuxtLink
        :to="'#' + contentSectionId.memecoin_explanation"
        class="info-btn"
        :aria-label="getContent('preview.info_button_aria_label')"
      >
        <AppIcon name="info_icon" />
      </NuxtLink>
      <div class="flex items-center justify-center gap-2">
        <span
          v-if="isTimerStarted"
          class="preview-content-text text-yellow font-bold uppercase"
        >
          {{ getContent('preview.button_block_title') }} {{ timeLeft }}
        </span>
        <span v-else class="preloader skeleton" />
      </div>
      <div class="preview-buttons-row flex mt-4 items-center">
        <AppBtn
          width="164"
          rounded="30"
          class="purple-btn"
          :is-processing="isUserDataLoading"
          :is-disabled="isButtonDisabled"
          :aria-label="getContent('preview.button_block_button_1_aria_label')"
          @click="openDialog"
        >
          <span>
            {{ buttonText }}
          </span>
        </AppBtn>
        <AppBtn
          width="164"
          rounded="30"
          class="green-btn"
          :aria-label="getContent('preview.button_block_button_2_aria_label')"
          @click="openComics"
        >
          <span>
            {{ getContent('preview.button_block_button_2') }}
          </span>
        </AppBtn>
      </div>
      <div
        v-if="Number(settings?.is_whitelist_open) === 0 && user?.status == null"
        class="text-center text-yellow mt-8"
      >
        <span class="additional-information-text">
          {{ getContent('preview.button_block_button_hint_1') }}
        </span>
      </div>
      <div v-else-if="user?.id">
        <div
          v-if="user?.status === defaultConstants.whitelistedStatus"
          class="mt-8"
        >
          <div class="additional-information-row with-mobile-indent">
            <span class="additional-information-text text-white col-1">
              Next step:
            </span>
            <span class="additional-information-text text-green">
              Please wait for allocation event before CNCLR presale.
            </span>
          </div>
          <div class="additional-information-row with-mobile-indent">
            <span class="additional-information-text text-white col-1">
              Updates:
            </span>
            <span class="additional-information-text text-white">
              Follow our social channels for the allocation date.
            </span>
          </div>
          <div class="additional-information-row with-mobile-indent">
            <span class="additional-information-text text-white col-1">
              Allocation:
            </span>
            <span class="additional-information-text text-white">
              Check your allocation and current presale stage:
              <NuxtLink
                target="_blank"
                :to="config.public.mainAppBaseUrl"
                class="additional-information-text text-yellow"
              >
                [HERE]
              </NuxtLink>
            </span>
          </div>
        </div>
        <div
          v-else-if="user?.status === defaultConstants.delistedStatus"
          class="mt-8"
        >
          <div class="additional-information-row with-mobile-indent">
            <span class="additional-information-text text-white col-1">
              Reason:
            </span>
            <span class="additional-information-text text-red">
              Required assets removed
            </span>
          </div>
          <div class="additional-information-row with-mobile-indent">
            <span class="additional-information-text text-white col-1">
              Action:
            </span>
            <span class="additional-information-text text-white">
              Top up your wallet to meet requirements
            </span>
          </div>
          <div class="additional-information-row with-mobile-indent">
            <span class="additional-information-text text-white col-1">
              Re-whitelisting:
            </span>
            <span class="additional-information-text text-white">
              Within 24 hours after requirements are met
            </span>
          </div>
        </div>
        <div class="additional-information-row mt-4">
          <span class="additional-information-text text-white col-1">
            Bonus:
          </span>
          <div class="flex gap-4">
            <PreviewSectionContentBonus
              icon="email_icon"
              :isAchieved="user?.is_email_claimed"
            />
            <PreviewSectionContentBonus
              icon="telegram_icon"
              :isAchieved="user?.is_telegram_claimed"
            />
            <PreviewSectionContentBonus
              icon="discord_icon"
              :isAchieved="user?.is_discord_claimed"
            />
            <PreviewSectionContentBonus
              icon="x_com_icon"
              :isAchieved="user?.is_twitter_claimed"
            />
          </div>
        </div>
      </div>
      <AppDialog
        v-model="isDialogVisible"
        open-query-param="is_modal"
        :open-query-value="whitelistDialogQueryValues"
        :title="getContent('whitelist_modal.title')"
      >
        <Transition>
          <LazyWhitelistDialogContent v-if="isDialogVisible" />
        </Transition>
      </AppDialog>
    </div>
    <p class="text-white preview-content-text">
      {{ getContent('preview.description_row_1') }}
      <br>
      {{ getContent('preview.description_row_2') }}
    </p>
    <DocumentLinks />
  </div>
</template>

<script setup>
useHead({
  link: [{
    rel: 'preload',
    as: 'image',
    href: '/preview_section_galaxy.webp',
  }]
})

const isDialogVisible = ref(false)
const whitelistDialogQueryValues = ['', '1', 'true', 'whitelist']

const config = useRuntimeConfig()
const settings = useState(appStateConstants.SETTINGS)
const user = useState(appStateConstants.USER)
const isComicsVisible = useState(appStateConstants.IS_COMICS_VISIBLE)
const isUserDataLoading = useState(appStateConstants.IS_USER_DATA_LOADING)

function openComics() {
  isComicsVisible.value = true
}

const {
  leftDays,
  leftHours,
  leftMinutes,
  leftSeconds,
  isStarted: isTimerStarted
} = useTimeLeft()

const timeLeft = computed(() => {
  return `${leftDays.value}:${leftHours.value}:${leftMinutes.value}:${leftSeconds.value}`
})

const buttonText = computed(() => {
  if (user.value?.id) {
    return user.value?.status || getContent('preview.button_block_button_1')
  } else {
    return getContent('preview.button_block_button_1')
  }
})

const isButtonDisabled = computed(() => Number(settings.value?.is_whitelist_open) === 0)

function openDialog() {
  if (isUserDataLoading.value) return
  isDialogVisible.value = true
}
</script>

<style lang="scss" scoped>
.preview-content {
  left: 50%;
  gap: 32px;
  z-index: 3;
  width: 100%;
  bottom: 32px;
  display: flex;
  padding: 0 16px;
  max-width: 1012px;
  text-align: center;
  position: absolute;
  align-items: center;
  justify-content: end;
  flex-direction: column;
  transform: translateX(-50%);

  .content-buttons-wrap {
    padding: 16px 32px 32px;
    text-align: center;
    border-radius: 16px;
    position: relative;

    .preview-buttons-row {
      justify-content: center;
      flex-direction: row;
      gap: 16px;
      min-width: 250px;
    }

    .info-btn {
      position: absolute;
      top: 20px;
      right: 32px;
    }
  }

  .additional-information-row {
    gap: 16px;
    display: flex;
    text-align: start;

    .col-1 {
      display: block;
      width: 85px;
    }
  }

  .additional-information-text {
    font-size: 14px;
    line-height: 24px;
  }
}
.preview-content.extended {
  top: -350px;
  bottom: auto;
}

@media only screen and (max-width: $sm) {
  .preview-content {
    left: 0;
    position: relative;
    transform: translate(0, -30px);

    .content-buttons-wrap {
      width: 100%;

      .preview-buttons-row {
        flex-direction: column;
        gap: 20px;
      }
    }

    .additional-information-row {
      gap: 0px;
      flex-direction: column;

      .col-1 {
        width: 100%;
      }

      &.with-mobile-indent {
        margin-top: 16px;
      }
    }
  }

  .preview-content.extended {
    top: auto;
    bottom: 32px;
  }
}
</style>
